import React, { useEffect, useState, useContext } from "react";
import classNames from "classnames";
import QRCodeStyling from "qr-code-styler";
import QrFrameWrapper from "../../framesQr/QrFrameWrapper";
import FrameText from "../../framesQr/frameText";
import { useDispatch, useSelector } from "react-redux";
import { deleteQrTemplate } from "../../../../../redux/requests";
import { defaultLogo } from "../../logotypeQr/svg";
import { setActiveQrTemplate } from "../../../../../redux/slices/qrTemplates";
import { qrSetFrames } from "../../../../../redux/slices/qrFrames";
import {
  qrLogotype,
  qrSetSettings,
  setBackgroundImage,
} from "../../../../../redux/slices/qrSettings";
import { qrSetText } from "../../../../../redux/slices/qrTextSettings";
import {
  blobToBase64,
  getBase64FromUrl,
  getBase64PngFromBase64PSvg,
} from "../../../../../hooks/helpers";
import { getBase64Logo } from "../../../../../hooks/helpersThirdStep";
import { CustomCol, FrameItem } from "../../framesQr/QrFrameItems";
import { setQrOptions } from "../../../../../redux/slices/qrOptions";
import { DefaultLogoContext } from "../../../index";
import { CloseIcon } from "../svg";
import useCurrentLocation from "../../../../../hooks/useCurrentLocation";
import { designTypes } from "../../../../../constants/common";

function SavedTemplate({ template, qrData }) {
  const qrTemplates = useSelector((state) => state.qrTemplates);
  const link = useSelector((state) => state.getQrLinkSlice.link);

  const {
    patternColor,
    pattern,
    cornetsOuter,
    cornetsOuterColor,
    cornetsInterior,
    cornetsInteriorColor,
    logotype,
    logotypeSize,
    logotypeMargin,
    logotypeBackground,
    isSingleColorDots,
    gradientDots,
    isSingleColorExternalEye,
    gradientExternalEye,
    isSingleColorInternalEye,
    gradientInternalEye,
    isSingleColorBackground,
    gradientBackground,
    qrBackgroundColor,
    backgroundImage,
    backgroundImageSize,
  } = template.qrSettings;

  const { setDefaultLogoData, setDefaultBackgroundImageData } =
    useContext(DefaultLogoContext);
  const dispatch = useDispatch();
  const { isTemplateService } = useCurrentLocation();

  const [qrImage, setQrImage] = useState("");

  const qrCode = new QRCodeStyling({
    width: 300,
    height: 300,
    type: "canvas",
  });

  useEffect(() => {
    if (!(link.length || qrData)) {
      return;
    }
    const getSvg =
      logotype && defaultLogo.find((findElem) => findElem.id === logotype);
    const getBgSvg =
      backgroundImage &&
      defaultLogo.find((findElem) => findElem.id === backgroundImage);
    const updateQrCode = async () => {
      qrCode.update({
        data: link || qrData,
        image: !!getSvg
          ? await getBase64Logo(logotype)
          : await getBase64PngFromBase64PSvg(logotype),
        dotsOptions: {
          color: patternColor,
          type: pattern,
          gradient: isSingleColorDots ? null : gradientDots,
        },
        cornersSquareOptions: {
          color: cornetsOuterColor,
          type: cornetsOuter,
          gradient: isSingleColorExternalEye ? null : gradientExternalEye,
        },
        cornersDotOptions: {
          color: cornetsInteriorColor,
          type: cornetsInterior,
          gradient: isSingleColorInternalEye ? null : gradientInternalEye,
        },
        imageOptions: {
          imageSize: logotypeSize,
          margin: logotypeMargin,
          hideBackgroundDots: logotypeBackground,
        },
        backgroundOptions: {
          gradient:
            isSingleColorBackground === false
              ? gradientBackground
              : {
                  colorStops: [
                    {
                      offset: 0,
                      color:
                        isSingleColorBackground == true
                          ? qrBackgroundColor
                          : "transparent",
                    },
                    {
                      offset: 1,
                      color:
                        isSingleColorBackground == true
                          ? qrBackgroundColor
                          : "transparent",
                    },
                  ],
                },
        },
        qrOptions: {
          errorCorrectionLevel:
            template?.qrOptions?.errorCorrectionLevel ?? "Q",
        },
        ...(template.designType === designTypes.art && {
          maskOptions: {
            drawMask: true,
            image: !!getBgSvg
              ? await getBase64Logo(backgroundImage)
              : await getBase64PngFromBase64PSvg(backgroundImage),
            imageSize: backgroundImageSize,
          },
        }),
      });
      const blob = await qrCode.getRawData();
      const base64Image = await blobToBase64(blob);
      setQrImage(base64Image);
    };
    updateQrCode();
  }, [qrTemplates, link, qrData]);

  const handleDeleteTemplate = (id) => {
    if (isTemplateService) {
      const url = new URL(window.location);
      const savedTemplatesDeleteUrl = url.searchParams.get(
        "savedTemplatesDeleteUrl"
      );
      dispatch(deleteQrTemplate({ id, savedTemplatesDeleteUrl }));
    } else {
      dispatch(deleteQrTemplate({ id }));
    }
  };

  const handleTemplateBtn = async () => {
    dispatch(setActiveQrTemplate(template.id));
    if (!qrData) {
      dispatch(qrSetFrames(template.qrFrames));
      dispatch(qrSetSettings(template.qrSettings));
      dispatch(
        qrSetText({
          ...template.qrText,
          textSize: template.qrText.textSize || -1,
        })
      );
      dispatch(setQrOptions(template.qrOptions));
      const logo = template.qrSettings.logotype;

      if (logo) {
        if (logo.includes("entryTemplate")) {
          const base64Img = await getBase64FromUrl(logo);
          dispatch(qrLogotype(base64Img));
        } else {
          const url = await getBase64Logo(logo);
          setDefaultLogoData(url);
        }
      }

      const backgroundImage = template.qrSettings.backgroundImage;
      if (backgroundImage) {
        if (backgroundImage.includes("backgroundImage")) {
          const base64Img = await getBase64FromUrl(backgroundImage);
          dispatch(setBackgroundImage(base64Img));
        } else {
          const url = await getBase64Logo(backgroundImage);
          setDefaultBackgroundImageData(url);
        }
      }
    }
  };

  return (
    <>
      <CustomCol xs={3} className="position-relative">
        <FrameItem
          onClick={handleTemplateBtn}
          className={classNames(
            "d-flex justify-content-center align-items-center border rounded-1 p-2 ",
            {
              "border-primary": template.id === qrTemplates.activeQrTemplate,
              "border-border-gray":
                template.id !== qrTemplates.activeQrTemplate,
            }
          )}
        >
          {!qrData && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTemplate(template.id);
              }}
              className="position-absolute top-0 end-0 border border-1 rounded-5 translate-middle-y d-flex justify-content-center align-items-center bg-white cursor-pointer"
              style={{ width: 25, height: 25, marginTop: 6 }}
            >
              <CloseIcon style={{ padding: 5 }} />
            </div>
          )}
          <div className="m-1">
            <svg
              viewBox="0 0 300 300"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: template.qrSettings
                  .patternBackgroundTransparent
                  ? "transparent"
                  : template.qrSettings.patternBackground,
              }}
            >
              <QrFrameWrapper
                frame={template.qrFrames.frame}
                frameColor={template.qrFrames.frameColor}
                isTransformFrameWithTextTemplate={
                  template.qrFrames.frameTextToggle
                }
              >
                <g transform={template.qrFrames.frameTransform}>
                  <foreignObject height="300" width="300">
                    <img src={qrImage} />
                  </foreignObject>
                </g>
              </QrFrameWrapper>

              {!!template.qrFrames.frameTextToggle && (
                <FrameText
                  text={template.qrText.frameText}
                  transform={template.qrFrames.transformFrameText}
                  textColor={template.qrFrames.frameTextColor}
                  textFont={template.qrFrames.frameTextFont}
                  textSize={template.qrText.textSize}
                />
              )}
            </svg>
          </div>
        </FrameItem>
      </CustomCol>
    </>
  );
}

export default SavedTemplate;
